import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Lock, ShieldQuestion } from 'lucide-react'

import { loanPrerequisitesQueryOptions, loanProductQueryOptions } from '@/api/loans-api'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorSurface } from '@/components/error-surface'

import {
  SAVINGS_DIRECTORY_UNAVAILABLE,
  gateActionLabel,
  gateDestinationTab,
  gateLabel,
} from './constants'
import { LoanLink } from './loan-link'
import { Field, Section } from './panel-primitives'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'

/**
 * The three gates a loan cannot pass without: an active savings account, a security deposit that
 * covers the round, and a usable passbook.
 *
 * Three things this panel refuses to blur together.
 *
 * **A gate that is unmet** is the ordinary case, and it names what the member has to bring.
 *
 * **A gate that could not be checked** is `loans.savings_directory_unavailable` — the Savings
 * module could not answer at all. The deposit may well be there and nobody can see it. Rendering
 * that as "requirements not met" would send a member home for a passbook they are holding, so it
 * gets its own panel and its own words.
 *
 * **A caller who may not ask** is the third. The endpoint is gated on `loans.account.open`, not
 * on `loans.view`, so a loan officer holding `loans.view`, `loans.apply`, even `loans.approve`,
 * gets a 403 here. That is a permission notice, not a failure — and certainly not "no gates met".
 */
export function LoanPrerequisitesPanel({
  customerId,
  productId,
  /** The principal the deposit is measured against. Omitted, the server uses the product minimum. */
  principalMinor,
  title = 'Prerequisites',
}: {
  customerId: string
  productId: string
  principalMinor?: number
  title?: string
}) {
  const permissions = useLoanPermissions()

  // The endpoint takes the required DEPOSIT, not the principal -- the percentage lives on the
  // product, so the translation happens here, and only as a preview: the binding figure freezes
  // on the cycle at approval, computed server-side.
  const product = useQuery({
    ...loanProductQueryOptions(productId, permissions.canViewProducts),
    enabled: productId !== '' && principalMinor !== undefined,
  })
  const requiredDepositMinor =
    principalMinor !== undefined && product.data
      ? Math.round((principalMinor * product.data.securityDepositPercent) / 100)
      : undefined

  const query = useQuery({
    ...loanPrerequisitesQueryOptions(customerId, productId, requiredDepositMinor),
    enabled:
      permissions.canOpenAccount
      && customerId !== ''
      && productId !== ''
      // With a principal in play, wait for the product so the gate is asked with the real figure
      // rather than answered twice.
      && (principalMinor === undefined || requiredDepositMinor !== undefined),
    // A 403 is an answer about this user, not a transient failure. Retrying it is noise.
    retry: false,
  })

  if (!permissions.canOpenAccount) {
    return (
      <Section title={title} description="The savings, deposit and passbook gates.">
        <Alert tone="info">
          <Lock aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The gate check is restricted</AlertTitle>
            <AlertDescription>
              Reading the gates needs{' '}
              <span className="font-mono text-xs">{LOAN_PERMISSIONS.accountOpen}</span>, which your
              role does not hold — the check belongs to account opening rather than to viewing a
              loan. The savings and deposit account numbers on the account record are what is
              visible to every reader.
            </AlertDescription>
          </div>
        </Alert>
      </Section>
    )
  }

  if (query.isPending) {
    return (
      <Section title={title} description="The savings, deposit and passbook gates.">
        <Skeleton className="h-32 w-full" />
      </Section>
    )
  }

  if (query.isError) {
    const error = query.error

    if (error.code === SAVINGS_DIRECTORY_UNAVAILABLE) {
      return (
        <Section title={title} description="The savings, deposit and passbook gates.">
          <Alert tone="warning">
            <ShieldQuestion aria-hidden />
            <div className="space-y-1">
              <AlertTitle>The gates could not be checked</AlertTitle>
              <AlertDescription>
                The Savings module did not answer, so this says nothing about whether the member
                passes. It is not a refusal and it is not a missing passbook — do not send anybody
                away on the strength of it. Try again, and raise it with support if it persists.
              </AlertDescription>
            </div>
          </Alert>
        </Section>
      )
    }

    return (
      <Section title={title} description="The savings, deposit and passbook gates.">
        <ErrorSurface
          error={error}
          onRetry={() => void query.refetch()}
          title="The gates could not be read"
        />
      </Section>
    )
  }

  // Belt and braces: a body that failed to arrive must degrade like an error, never take the
  // whole route down -- this exact undefined once crashed the open-account screen. Typed around
  // the narrowing deliberately: the runtime state existed even where the types said it could not.
  const prerequisites = query.data as typeof query.data | undefined
  if (prerequisites === undefined) {
    return (
      <Section title={title} description="The savings, deposit and passbook gates.">
        <Alert tone="warning">
          <ShieldQuestion aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The gates could not be read</AlertTitle>
            <AlertDescription>
              The server answered without a body. This says nothing about whether the member
              passes — try again.
            </AlertDescription>
          </div>
        </Alert>
      </Section>
    )
  }

  const met = prerequisites.unmetGates.length === 0

  return (
    <Section
      title={title}
      description="Answered by the Savings module. An unanswerable gate is a refusal, never a pass."
    >
      <div className="space-y-4">
        {met ? (
          <Alert tone="success">
            <CheckCircle2 aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Every gate is met</AlertTitle>
              <AlertDescription>
                Checked against{' '}
                {principalMinor === undefined
                  ? 'the accounts and passbook alone, which is what account opening asks about'
                  : 'the deposit this principal implies — a preview'}
                . The binding figure freezes at approval and is re-checked at disbursement.
              </AlertDescription>
            </div>
          </Alert>
        ) : (
          <Alert tone="warning">
            <AlertTriangle aria-hidden />
            <div className="space-y-1">
              <AlertTitle>
                {prerequisites.unmetGates.length === 1
                  ? 'One gate is not met'
                  : `${prerequisites.unmetGates.length} gates are not met`}
              </AlertTitle>
              <AlertDescription>
                <ul className="space-y-1.5">
                  {prerequisites.unmetGates.map((code) => (
                    <li key={code} className="space-y-0.5">
                      <div>
                        {gateLabel(code)}{' '}
                        <span className="font-mono text-2xs text-muted">{code}</span>
                      </div>
                      {/*
                        The gate says what is missing; this says where to go and get it. Without
                        it the operator is told "issue a passbook" by a screen that cannot issue
                        one, with nothing naming the screen that can.
                      */}
                      <LoanLink
                        to={`/savings/customers/${customerId}?tab=${gateDestinationTab(code)}`}
                        className="inline-flex items-center text-sm font-medium text-brand-text underline underline-offset-2 hover:text-brand-text-hover max-lg:min-h-11"
                      >
                        {gateActionLabel(code)} →
                      </LoanLink>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </div>
          </Alert>
        )}

        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Savings account"
            value={
              prerequisites.savingsAccount ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs">
                    {prerequisites.savingsAccount.accountNumber}
                  </span>
                  <StatusBadge status={prerequisites.savingsAccount.status} />
                </span>
              ) : null
            }
            hint={prerequisites.savingsAccount ? undefined : 'None on file.'}
          />
          <Field
            label="Security deposit account"
            value={
              prerequisites.securityDepositAccount ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs">
                    {prerequisites.securityDepositAccount.accountNumber}
                  </span>
                  <StatusBadge status={prerequisites.securityDepositAccount.status} />
                </span>
              ) : null
            }
            hint={prerequisites.securityDepositAccount ? undefined : 'None on file.'}
          />
          <Field
            label="Deposit held"
            value={
              <MoneyDisplay
                value={prerequisites.securityDepositBalanceMinor}
                currency={prerequisites.currencyCode}
                className="text-left"
              />
            }
            hint="Not withdrawable. It is released by an offset or at closure."
          />
          <Field
            label="Passbook"
            value={
              prerequisites.passbook ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs">{prerequisites.passbook.passbookNumber}</span>
                  <StatusBadge status={prerequisites.passbook.status} />
                </span>
              ) : null
            }
            hint={
              prerequisites.passbook
                ? 'Books this one replaced are held by the Savings module, not here.'
                : 'None issued, or the issued one is lost, replaced or retired.'
            }
          />
        </dl>
      </div>
    </Section>
  )
}
